import { useNavigate } from "react-router-dom";
import dateformat from "dateformat";

import FileSaver from "file-saver";

import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";

import { useSelector } from "react-redux";
import { TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import useAuth from "$hooks/useAuth";
import { DispositionType, ShippingRecordStatus } from "$types";

import {
  getShipmentProofDeliveryOptions,
  getShippingRecordStatusColor,
  getShippingRecordStatusLabel,
  getShippingRecordStatusLabelOptions,
} from "$utils/typeConstants";
import {
  DEFAULT_DATE_FORMAT,
  normalizeCarrierTrackingLink,
} from "$utils/utils";

import { Button, Text, StatusBadge } from "$components";

import { theme } from "$utils/theme/bataillog";
import { ShipmentService } from "$services";
import { appState } from "$reducers";
import { DataTableProvider, EnumFilterTypes } from "$components/DataTable";
import { useStyles } from "./ShippingRecordsTracking.styles";
import JTable from "$components/JTable/JTable";
import CompanyFilterInput from "$forms/CompanyFilterInput";
import useDebounceValue from "$hooks/useDebounceValue";
import GenericSelect from "$forms/GenericSelect";
import useJStyles from "$hooks/useJStyles";
import BataillogDatePicker from "$components/BataillogDatePicker/BataillogDatePicker";
import { CacheInvalidationAction } from "$utils/cache";

export const ShippingRecordsTracking = ({
  handleUrlParams,
  urlParams,
}: any) => {
  const { filter: companySidebarFilter } = useSelector(appState);
  const { isAdmin } = useAuth();
  return (
    <DataTableProvider
      externalState={{
        filters: {
          ...(isAdmin() &&
            companySidebarFilter?.name && {
              company: companySidebarFilter?.name,
            }),
          status: [
            ShippingRecordStatus.TO_SHIP,
            ShippingRecordStatus.SHIPPED,
            ShippingRecordStatus.DELIVERED,
          ],
        },
      }}
    >
      <ShippingRecordsTrackingInside
        handleUrlParams={handleUrlParams}
        urlParams={urlParams}
      />
    </DataTableProvider>
  );
};

const defaultValuesFilter = {
  shippingRecordCode: null,
  company: null,
  refEntrepotAndOpCode: null,
  shippingRecordStatuses: [
    ShippingRecordStatus.TO_SHIP,
    ShippingRecordStatus.SHIPPED,
    ShippingRecordStatus.DELIVERED,
    ShippingRecordStatus.COLLECTED,
  ],
};

export const ShippingRecordsTrackingInside = ({
  handleUrlParams,
  urlParams,
}: any) => {
  const { userRole } = useAuth();
  const { jclasses } = useJStyles();
  const classes = useStyles();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const { filter: companySidebarFilter } = useSelector(appState);
  const paramsToUrl = {};
  Array.from((urlParams as URLSearchParams).entries()).forEach(
    ([key, value]) => {
      paramsToUrl[key] = value === "null" ? null : value;
    }
  );

  const [filters, setFilters] = useState<any>({
    ...defaultValuesFilter,
    ...paramsToUrl,
  });
  const [dateStart_tfilter, setDateStart_tfilter] = useState<
    Date | undefined | null
  >(null);
  const [dateEnd_tfilter, setDateEnd_tfilter] = useState<
    Date | undefined | null
  >(null);

  // Clear company filter when sidebar filter is first activated
  useEffect(() => {
    if (companySidebarFilter?._id && filters.company) {
      setFilters((prev) => ({ ...prev, company: null }));
      handleUrlParams("company", null);
    }
  }, [companySidebarFilter?._id, filters.company, handleUrlParams]);

  const debouncedFilters = useDebounceValue(
    {
      ...filters,
      ...(companySidebarFilter?._id
        ? { company: companySidebarFilter.name }
        : filters.company && filters.company !== "null"
          ? { company: filters.company }
          : {}),
    },
    500
  );

  const { data: { shipments, total } = {}, isLoading } = useQuery({
    queryKey: [
      CacheInvalidationAction.GET_SHIPMENTS_WITH_OPS_AND_COMPANIES,
      page,
      debouncedFilters,
    ],

    queryFn: ({ signal }) =>
      ShipmentService.getShipmentsWithOpsAndCOmpanies(
        {
          ...debouncedFilters,
          pagination: { page: page + 1, pageSize: 20 },
        },
        signal
      ),

    gcTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const headerData = [
    {
      id: "company",
      filterName: "company",
      Header: "Entreprise",
      filterComponent: (props: any) => {
        return (
          <CompanyFilterInput
            value={companySidebarFilter?.name || props.value || ""}
            label=""
            placeholder={companySidebarFilter?.name || "Par nom"}
            onChange={(companyName) => {
              if (companyName && companyName.trim()) {
                setFilters({ ...filters, company: companyName });
                handleUrlParams("company", companyName);
              } else {
                setFilters({ ...filters, company: null });
                handleUrlParams("company", null);
              }
            }}
            disabled={!!companySidebarFilter?.name}
          />
        );
      },
      minWidth: 170,
      Cell: ({ row: { original: shipment } }: any) => {
        const shipmentCompanyName = shipment?.op?.company?.name || "N/A";
        return (
          <Text
            className={shipment.disabled ? classes.disabledCell : classes.cell}
            type={shipment?.op?.company?.isActive ? "important" : "disabled"}
            style={{
              whiteSpace: "nowrap",
              display: "inline-block",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {shipmentCompanyName}
          </Text>
        );
      },
    },
    {
      id: "shippingRecordCode",
      Header: "N° de dossier",
      filterName: "shippingRecordCode",
      filterComponent: (props: any) => {
        return (
          <TextField
            placeholder="Par N° de dossier"
            variant="outlined"
            type="text"
            value={props.value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              props.onChange(e.target.value);
              setFilters((prevFilters) => ({
                ...prevFilters,
                shippingRecordCode: e.target.value,
              }));
              handleUrlParams("shippingRecordCode", e.target.value);
            }}
          />
        );
      },
      minWidth: 150,
      Cell: ({ row: { original: shipment } }: any) => {
        return (
          <Text
            type="important"
            className={shipment.disabled ? classes.disabledCell : classes.cell}
          >
            {shipment?.shippingRecord?.code}
          </Text>
        );
      },
    },
    {
      id: "refEntrepotAndOpCode",
      Header: "Num OP Ref prépa entr.",
      filterName: "refEntrepotAndOpCode",
      filterComponent: (props: any) => {
        return (
          <TextField
            placeholder="Par Num OP Ref prépa entr."
            variant="outlined"
            type="text"
            value={props.value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              props.onChange(e.target.value);
              setFilters((prevFilters) => ({
                ...prevFilters,
                refEntrepotAndOpCode: e.target.value,
              }));
              handleUrlParams("refEntrepotAndOpCode", e.target.value);
            }}
          />
        );
      },
      filter: {
        type: EnumFilterTypes.INPUT,
        label: "Num OP \nRef prépa entr.",
        valueFilter: ({ op }) =>
          `${op?.code ?? ""} ${op?.refPreparationEntrepot ?? ""}`,
      },
      minWidth: 220,
      Cell: ({ row: { original: shipment } }: any) => {
        return (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div>
              <Text
                style={{ fontWeight: 700 }}
                className={
                  shipment?.disabled ? classes.disabledCell : classes.cell
                }
                type={shipment?.disabled ? "text" : "important"}
              >
                {shipment?.op?.code || "N/A"}
              </Text>
            </div>
            <div>{shipment?.op?.refPreparationEntrepot || "N/A"}</div>
          </div>
        );
      },
    },
    {
      id: "edbId",
      Header: "N° EDB",
      filterName: "edbId",
      filterComponent: (props: any) => {
        return (
          <TextField
            placeholder="Par N° EDB"
            variant="outlined"
            type="text"
            value={props.value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              props.onChange(e.target.value);
              setFilters((prevFilters) => ({
                ...prevFilters,
                edbId: e.target.value,
              }));
              handleUrlParams("edbId", e.target.value);
            }}
          />
        );
      },
      minWidth: 120,
      Cell: ({ row: { original: shipment } }: any) => {
        return (
          <Text
            type="important"
            className={shipment.disabled ? classes.disabledCell : classes.cell}
          >
            {shipment?.op?.edb?.edbId || "N/A"}
          </Text>
        );
      },
    },
    {
      id: "shipingLocation",
      Header: "Lieu enlèvement",
      filterName: "shippingLocation",
      filterComponent: (props: any) => {
        return (
          <TextField
            placeholder="Par lieu enlèvement"
            variant="outlined"
            type="text"
            value={props.value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              props.onChange(e.target.value);
              setFilters((prevFilters) => ({
                ...prevFilters,
                shippingLocation: e.target.value,
              }));
              handleUrlParams("shippingLocation", e.target.value);
            }}
          />
        );
      },
      minWidth: 200,
      Cell: ({ row: { original: shipment } }: any) => {
        const pickupName = shipment?.op?.entrepot?.name;
        const pickupLocation = [
          shipment?.op?.entrepot?.location?.cp,
          shipment?.op?.entrepot?.location?.city,
        ]
          .filter(Boolean)
          .join(" ");
        if (!pickupName && !pickupLocation) {
          return (
            <Text
              type="important"
              className={
                shipment.disabled ? classes.disabledCell : classes.cell
              }
            >
              N/A
            </Text>
          );
        }
        return (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div>
              <Text
                style={{ fontWeight: 700 }}
                className={
                  shipment?.disabled ? classes.disabledCell : classes.cell
                }
                type={shipment?.disabled ? "text" : "important"}
              >
                {pickupName || "N/A"}
              </Text>
            </div>
            <div>{pickupLocation || "N/A"}</div>
          </div>
        );
      },
    },
    {
      id: "deliveryLocation",
      Header: "Lieu livraison",
      filterName: "deliveryLocation",
      filterComponent: (props: any) => {
        return (
          <TextField
            placeholder="Par lieu livraison"
            variant="outlined"
            type="text"
            value={props.value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              props.onChange(e.target.value);
              setFilters((prevFilters) => ({
                ...prevFilters,
                deliveryLocation: e.target.value,
              }));
              handleUrlParams("deliveryLocation", e.target.value);
            }}
          />
        );
      },
      minWidth: 200,
      Cell: ({ row: { original: shipment } }: any) =>
        shipment?.op?.shipType === DispositionType.ON_DOCKYARD ? (
          <div
            className={shipment.disabled ? classes.disabledCell : classes.cell}
          >
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div>
                <Text
                  className={
                    shipment.disabled ? classes.disabledCell : classes.cell
                  }
                  type={shipment.disabled ? "text" : "important"}
                  style={{ fontWeight: 700 }}
                >
                  {shipment?.op?.dockyard?.code ?? "N/A"}
                </Text>
                {shipment?.op?.dockyard?.name
                  ? ` ${shipment.op.dockyard.name}`
                  : ""}
              </div>
              <div>
                {[shipment?.op?.shipPostalCode, shipment?.op?.shipCity]
                  .filter(Boolean)
                  .join(" ") || "N/A"}
              </div>
            </div>
          </div>
        ) : (
          <div
            className={shipment.disabled ? classes.disabledCell : classes.cell}
          >
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div>
                <Text
                  className={
                    shipment.disabled ? classes.disabledCell : classes.cell
                  }
                  type={shipment.disabled ? "text" : "important"}
                  style={{ fontWeight: 700 }}
                >
                  {shipment?.op?.shipEntrepotName ||
                    shipment?.op?.shipPlace ||
                    "N/A"}
                </Text>
              </div>
              <div>
                {[shipment?.op?.shipPostalCode, shipment?.op?.shipCity]
                  .filter(Boolean)
                  .join(" ") || "N/A"}
              </div>
            </div>
          </div>
        ),
    },
    {
      id: "datesRange",
      Header: "Date d'expédition\nDate de livraison",
      filterName: "datesRange",
      filterComponent: () => (
        <BataillogDatePicker
          selectsRange
          startDate={dateStart_tfilter}
          endDate={dateEnd_tfilter}
          onDateChange={(dates: Date | [Date, Date]) => {
            if (Array.isArray(dates)) {
              const [start, end] = dates;
              setDateStart_tfilter(start);
              setDateEnd_tfilter(end);
              if (!start && !end) {
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  startDate: start,
                }));
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  endDate: end,
                }));
                setDateStart_tfilter(null);
                setDateEnd_tfilter(null);
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  endDate: end,
                }));
              } else if (start === end || !end) {
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  shippingStart: dateformat(start, "yyyy-mm-dd"),
                }));
              } else {
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  startDate: dateformat(start, "yyyy-mm-dd"),
                }));
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  endDate: dateformat(end, "yyyy-mm-dd"),
                }));
              }
            }
          }}
        />
      ),
      minWidth: 160,
      Cell: ({ row: { original: shipment } }: any) => {
        const dateStart = shipment?.shippingRecord?.statusHistory.find(
          (history) => history.status === ShippingRecordStatus.SHIPPED
        )?.date;
        const dateStop = shipment?.shippingRecord?.statusHistory.find(
          (history) => history.status === ShippingRecordStatus.DELIVERED
        )?.date;
        return (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Text
              className={
                shipment.disabled ? classes.disabledCell : classes.cell
              }
            >
              {dateStart ? dateformat(dateStart, DEFAULT_DATE_FORMAT) : "N/A"}
            </Text>
            <Text
              className={
                shipment.disabled ? classes.disabledCell : classes.cell
              }
            >
              {dateStop ? dateformat(dateStop, DEFAULT_DATE_FORMAT) : "N/A"}
            </Text>
          </div>
        );
      },
    },
    {
      id: "commissionAndCarrier",
      Header: "Commissionnaire\nTransporteur",
      filterName: "commissionAndCarrier",
      filterComponent: (props: any) => {
        return (
          <TextField
            placeholder="Par commissionnaire ou transporteur"
            variant="outlined"
            type="text"
            value={props.value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              props.onChange(e.target.value);
              setFilters((prevFilters) => ({
                ...prevFilters,
                commissionAndCarrier: e.target.value,
              }));
              handleUrlParams("commissionAndCarrier", e.target.value);
            }}
          />
        );
      },
      minWidth: 150,
      Cell: ({ row: { original: shipment } }: any) => {
        return (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Text
              className={
                shipment.disabled ? classes.disabledCell : classes.cell
              }
            >
              {shipment?.shippingRecord?.commissionCompany?.name ?? ""}
            </Text>
            <Text
              className={
                shipment.disabled ? classes.disabledCell : classes.cell
              }
            >
              {shipment?.shippingRecord?.carrierCompany?.name ?? ""}
            </Text>
          </div>
        );
      },
    },
    {
      id: "carrierReference",
      Header: "Ref transporteur",
      filterName: "carrierReference",
      filterComponent: (props: any) => {
        return (
          <TextField
            placeholder="Par ref transporteur"
            variant="outlined"
            type="text"
            value={props.value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              props.onChange(e.target.value);
              setFilters((prevFilters) => ({
                ...prevFilters,
                carrierReference: e.target.value,
              }));
              handleUrlParams("carrierReference", e.target.value);
            }}
          />
        );
      },
      minWidth: 150,
      Cell: ({ row: { original: shipment } }: any) => {
        return (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Text
              className={
                shipment.disabled ? classes.disabledCell : classes.cell
              }
            >
              {shipment?.shippingRecord?.carrierReference ?? ""}
            </Text>
          </div>
        );
      },
    },
    {
      id: "carrierTrackingLink",
      Header: () => (
        <span style={{ display: "inline-block", lineHeight: 1.1 }}>
          LIEN SUIVI
          <br />
          TRANSPORTEUR
        </span>
      ),
      filterName: "carrierTrackingLink",
      filterComponent: () => null,
      minWidth: 80,
      Cell: ({ row: { original: shipment } }: any) => {
        const href = normalizeCarrierTrackingLink(
          shipment?.shippingRecord?.carrierTrackingLink ?? ""
        );
        const displayHref =
          href && href.length > 25 ? `${href.slice(0, 25)}…` : href;
        if (!href) {
          return (
            <Text
              className={
                shipment.disabled ? classes.disabledCell : classes.cell
              }
            />
          );
        }
        return (
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className={shipment.disabled ? classes.disabledCell : classes.cell}
            style={{
              whiteSpace: "nowrap",
              display: "inline-block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "100%",
              color: "inherit",
              textDecoration: "underline",
            }}
            title={href}
            onClick={(e) => {
              // keep default navigation but prevent row click selection from capturing
              e.stopPropagation();
            }}
          >
            {displayHref}
          </a>
        );
      },
    },
    {
      id: "price",
      Header: "Tarifs",
      filterName: "price",
      filterComponent: (props: any) => {
        return (
          <TextField
            placeholder="Par tarifs"
            variant="outlined"
            type="text"
            value={props.value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              props.onChange(e.target.value);
              setFilters((prevFilters) => ({
                ...prevFilters,
                price: e.target.value,
              }));
              handleUrlParams("price", e.target.value);
            }}
          />
        );
      },
      minWidth: 120,
      Cell: ({ row: { original: shipment } }: any) => {
        return (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Text
              className={
                shipment.disabled ? classes.disabledCell : classes.cell
              }
            >
              {shipment?.shippingRecord?.price ?? ""}
            </Text>
          </div>
        );
      },
    },
    {
      id: "proofOfDelivery",
      Header: "Preuve de livraison",
      filterName: "proofOfDelivery",
      filterComponent: (props: any) => {
        return (
          <GenericSelect
            className={jclasses.jTableLabelHeader}
            onChange={(val) => {
              props.onChange(val);
              setFilters((prevFilters) => ({
                ...prevFilters,
                proofOfDelivery: val,
              }));
              handleUrlParams("proofOfDelivery", val);
            }}
            value={props.value}
            options={getShipmentProofDeliveryOptions()}
          />
        );
      },
      minWidth: 120,
      Cell: ({ row: { original: shipment } }: any) => {
        const proofOfDeliveryRaw = shipment?.proofOfDelivery;
        const proofFiles = Array.isArray(proofOfDeliveryRaw)
          ? proofOfDeliveryRaw
          : proofOfDeliveryRaw
            ? [proofOfDeliveryRaw]
            : [];
        return proofFiles.length > 0 ? (
          <Button
            variant="text"
            className={classes.proofOfDeliveryButton}
            onClick={() =>
              FileSaver.saveAs(
                proofFiles[0].url,
                proofFiles[0].name || "preuve_livraison.pdf"
              )
            }
          >
            Reçue
          </Button>
        ) : (
          "Non reçue"
        );
      },
    },
    {
      id: "status",
      Header: "Statut dossier",
      filterName: "shippingRecordStatuses",
      filterComponent: (props: any) => {
        return (
          <GenericSelect
            className={jclasses.jTableLabelHeader}
            onChange={(val) => {
              props.onChange(val);
              setFilters((prevFilters) => ({
                ...prevFilters,
                shippingRecordStatuses: val,
              }));
              handleUrlParams("shippingRecordStatuses", val);
            }}
            value={filters.shippingRecordStatuses || props.value}
            options={getShippingRecordStatusLabelOptions(userRole, [
              ShippingRecordStatus.DRAFT,
            ])}
            multiple
          />
        );
      },
      filter: {
        type: EnumFilterTypes.SELECT,
        label: "Statut dossier",
        multiple: true,
        valueFilter: ({ shippingRecord }) => shippingRecord.status,
        options: getShippingRecordStatusLabelOptions(userRole, [
          ShippingRecordStatus.DRAFT,
        ]),
      },
      minWidth: 120,
      Cell: ({ row: { original: shipment } }: any) => {
        return (
          <StatusBadge
            text={getShippingRecordStatusLabel(
              userRole,
              shipment?.shippingRecord?.status
            )}
            fullWidth
            colorManual={getShippingRecordStatusColor(
              shipment?.shippingRecord?.status,
              theme
            )}
          />
        );
      },
    },
    {
      id: "dispute",
      Header: "Litiges",
      filterName: "dispute",
      filterComponent: (props: any) => {
        return (
          <GenericSelect
            className={jclasses.jTableLabelHeader}
            onChange={(val) => {
              props.onChange(val);
              setFilters((prevFilters) => ({
                ...prevFilters,
                dispute: val,
              }));
              handleUrlParams("dispute", val);
            }}
            value={props.value}
            options={[
              { value: "true", label: "Avec litiges" },
              { value: "false", label: "Sans litiges" },
            ]}
          />
        );
      },
      minWidth: 100,
      Cell: ({ row: { original: shipment } }: any) => {
        if (!shipment?.shippingRecord?.shippingRecordDisputes) {
          return "";
        }
        return shipment?.shippingRecord?.shippingRecordDisputes.some(
          (dispute: any) => Boolean(!dispute.closedAt)
        ) ? (
          <SentimentVeryDissatisfiedIcon style={{ color: "tomato" }} />
        ) : (
          <SentimentSatisfiedAltIcon style={{ color: "green" }} />
        );
      },
    },
    {
      Header: null,
      accessor: "expander",
      minWidth: 30,
      maxWidth: 30,
      preventClickAction: true,
    },
  ];

  const pageCount = Math.ceil(total / 20);

  return (
    <JTable
      hideCheckboxes
      data={shipments ?? []}
      columns={headerData}
      isFetching={isLoading}
      filters={filters}
      // setFilter={setFilters}
      setPageIndex={setPage}
      pageIndex={page}
      pageCount={pageCount}
      onClickAction={() => {}}
      rowProps={({ original: shipment }: any) => ({
        onClick: (e: React.MouseEvent<HTMLTableRowElement>) => {
          // Don't navigate if clicking on interactive elements or the expander column
          const target = e.target as HTMLElement;

          // Check if clicking on expander column (last column with no data-row)
          const clickedCell = target.closest("td");
          if (clickedCell) {
            const row = e.currentTarget;
            const cells = Array.from(row.querySelectorAll("td"));
            const isLastCell = cells[cells.length - 1] === clickedCell;
            const dataRow = clickedCell.getAttribute("data-row");
            // Expander is the last cell with no data-row attribute
            if (isLastCell && (!dataRow || dataRow === "")) {
              return;
            }
          }

          if (shipment?.shippingRecord?.id) {
            navigate(`/expeditions/${shipment.shippingRecord.id}`);
          }
        },
        style: { cursor: "pointer" },
      })}
      // renderRowSubComponent={renderRowSubComponent}
      // setSelectedItem={setSelectedOPs}
      // setSelectedRows={setSelectedRows}
      pageName="shipmentList"
      noDataText="Aucun shipment n'a encore été créée"
    />
  );
};
